import React, { useState,useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Radio,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Paper,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  AlertTitle,
  Snackbar
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { EmailApiService } from "../services/EmailApiService";
import { useParams } from "react-router-dom";
import { API_CONFIG, API_ENDPOINTS } from "../constants/apiConfig";



const Questions = () => {
  const { id } = useParams();
  const theme = useTheme();
  const hasFetched = useRef(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: 'success' as 'success' | 'error',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
  quoteDeadline: "",
  orderTimeline: "",
  projectStage: "",
  leaktest: "",
  material: "",
  runout: "",
  postweldmachining: "",
  assemblyWeight: "",
  preassembled: "",
  eau: "",
  industry: "",
  risks: "",
  billingAddress: "",
  shippingAddress: "",
  hearAboutUs: "",
  outsourceWelding: "",
  outsourceDetails: "",
  weldPenetrationDepth: "",
});
useEffect(() => {
  if (!hasFetched.current) {
    getQuestions();
    hasFetched.current = true;
  }
}, []);

useEffect(() => {
  if (!alert.open) return;

  window.scrollTo({ top: 0, behavior: 'smooth' });

  const timer = setTimeout(() => {
    setAlert((prev) => ({ ...prev, open: false }));
  }, 3000);

  return () => clearTimeout(timer);
}, [alert.open]);

const api = new EmailApiService();

const getQuestions = async () => {
  try {
    if (!id) return;

    const response: any = await api.getQuestionById(id);

    // Check if the response contains the specific message
    if (response?.data?.message === "No answer data found for the given email") {
      console.log(response.data.message);
      return;
    }

    const answerData = response?.data?.data?.answer_json;

    const formatDateForInput = (dateStr: string) => {
      if (!dateStr) return "";
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[0]}-${parts[1]}`;
      }
      return dateStr;
    };

    const formattedOrderTimeline = formatDateForInput(answerData?.initial_order_timeline || "");
    console.log("Raw orderTimeline:", answerData?.initial_order_timeline);
    console.log("Formatted orderTimeline:", formattedOrderTimeline);

    setFormData({
      quoteDeadline: formatDateForInput(answerData?.quote_deadline || ""),
      orderTimeline: formatDateForInput(answerData?.initial_order_timeline || ""),
      projectStage: answerData?.project_stage || "",
      leaktest: answerData?.fine_leak_testing_required || "",
      material: answerData?.preheat_concerns || "",
      runout: answerData?.runout_requirement || "",
      postweldmachining: answerData?.post_weld_machining_required || "",
      assemblyWeight: answerData?.assembly_weight_lbs || "",
      preassembled: answerData?.parts_clean_and_preassembled || "",
      eau: answerData?.estimated_annual_usage_quantity || "",
      industry: answerData?.industry_application || "",
      risks: answerData?.project_risks_or_special_handling || "",
      billingAddress: answerData?.billing_address || "",
      shippingAddress: answerData?.shipping_address || "",
      hearAboutUs: answerData?.hear_about_eb_industries || "",
      outsourceDetails:
        answerData?.additional_outsourced_welding_assemblies || "",
      weldPenetrationDepth:
        answerData?.required_weld_penetration_depth || "",
      outsourceWelding: "",
    });
  } catch (error) {
    console.log(error);
  }
};

const handleChange = (field: string, value: string) => {
  setFormData((prev) => ({
    ...prev,
    [field]: value,
  }));
};

const getQuestionNumber = (baseNumber: number) => {
  let offset = 0;
  if (formData.weldPenetrationDepth) offset++;
  if (formData.assemblyWeight && baseNumber >= 7) offset++;
  if (formData.leaktest && baseNumber >= 5) offset++;
  return baseNumber - offset;
};

const isFormValid = () => {
  // Always required fields
  const alwaysRequired = [
    formData.quoteDeadline,
    formData.orderTimeline,
    formData.projectStage,
    formData.postweldmachining,
    formData.preassembled,
    formData.industry,
  ];

  // Conditionally required fields (only check if they are shown)
  const conditionalRequired = [];
  if (!formData.weldPenetrationDepth) {
    conditionalRequired.push(formData.weldPenetrationDepth);
  }
  if (!formData.assemblyWeight) {
    conditionalRequired.push(formData.assemblyWeight);
  }
  if (!formData.leaktest) {
    conditionalRequired.push(formData.leaktest);
  }
  conditionalRequired.push(formData.eau);

  const allRequired = [...alwaysRequired, ...conditionalRequired];

  return allRequired.every(field => field && field.trim() !== '');
};

const handleSubmit = async () => {
  console.log(formData);

  if (!isFormValid()) {
    setAlert({ open: true, severity: 'error', message: 'Please fill all required fields' });
    return;
  }

  setIsSubmitting(true);

  const formatDateForSubmission = (dateStr: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return dateStr;
  };

  const payload = {
    data: {
      eb_emails_id_fk: id,
      answer_json: {
        additional_outsourced_welding_assemblies: formData.outsourceDetails,
        assembly_weight_lbs: formData.assemblyWeight,
        billing_address: formData.billingAddress,
        estimated_annual_usage_quantity: formData.eau,
        fine_leak_testing_required: formData.leaktest,
        hear_about_eb_industries: formData.hearAboutUs,
        industry_application: formData.industry,
        initial_order_timeline: formData.orderTimeline,
        parts_clean_and_preassembled: formData.preassembled,
        post_weld_machining_required: formData.postweldmachining,
        preheat_concerns: formData.material,
        project_risks_or_special_handling: formData.risks,
        project_stage: formData.projectStage,
        quote_deadline: formatDateForSubmission(formData.quoteDeadline),
        required_weld_penetration_depth: formData.weldPenetrationDepth,
        runout_requirement: formData.runout,
        shipping_address: formData.shippingAddress,
      },
    },
  };

  try {
    const endpoint = API_ENDPOINTS.ANSWERS.WEB_EDITED_ANSWERS;
    const url = `${API_CONFIG.ANSWERS_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData?.message || errorData?.error || `HTTP error! status: ${response.status}`;
      console.error('Error submitting answers:', errorMessage);
      setAlert({ open: true, severity: 'error', message: errorMessage });
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('Answers submitted successfully:', data);
    setAlert({ open: true, severity: 'success', message: 'Answers submitted successfully!' });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error submitting answers. Please try again.';
    console.error('Error submitting answers:', errorMessage);
    setAlert({ open: true, severity: 'error', message: errorMessage });
  } finally {
    setIsSubmitting(false);
  }
};
  return (
    <Box sx={{  p: 2 }}>
        <Snackbar
          open={alert.open}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          <Alert
            severity={alert.severity}
            sx={{
              width: '400px',
              backgroundColor: alert.severity === 'success' ? theme.palette.success.main : theme.palette.error.main,
              color: '#fff'
            }}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            {alert.message}
          </Alert>
        </Snackbar>
        <Box sx={{width:'60%',margin:'auto',backgroundColor:'#ffff',padding:'20px',borderRadius:'5px'}}>
          <Box sx={{ mb: 4 }}>
  <Typography
    sx={{
      textAlign: "left",
      color: "rgb(148, 163, 184)",
      fontSize: "18px",
      mb: 1,
    }}
  >
    Question 1*
  </Typography>

  <Typography
    sx={{
      mb: 2,
      color: "#0f172a",
      fontSize: "16px",
      textAlign: "left",
    }}
  >
    Do you have a deadline for when you need the quote back?
  </Typography>

  <TextField
    fullWidth
    type="date"
    required
    value={formData.quoteDeadline}
onChange={(e) =>
  handleChange("quoteDeadline", e.target.value)
}
    InputLabelProps={{ shrink: true }}
    sx={{border:'1px solid black',borderRadius:'15px'}}
  />
</Box>

        <Box sx={{ mb: 4 }}>
  <Typography
    sx={{
      textAlign: "left",
      color: "rgb(148, 163, 184)",
      fontSize: "18px",
      mb: 1,
    }}
  >
    Question 2*
  </Typography>

  <Typography
    sx={{
      mb: 2,
      color: "#0f172a",
      fontSize: "16px",
      textAlign: "left",
    }}
  >
    When do you anticipate placing the initial order if this quote moves forward?
  </Typography>

  <TextField
    fullWidth
    required
    type="date"
    value={formData.orderTimeline}
onChange={(e) =>
  handleChange("orderTimeline", e.target.value)
}
    InputLabelProps={{ shrink: true }}
    sx={{border:'1px solid black',borderRadius:'15px'}}
  />
</Box>

          <Box>
  <Typography
    sx={{
      textAlign: "left",
      color: "rgb(148, 163, 184)",
      fontSize: "18px",
      mb: 1,
    }}
  >
    Question 3*
  </Typography>

  <Typography
    sx={{
      mb: 3,
      color: "#0f172a",
      fontSize: "16px",
      textAlign: "left",
    }}
  >
    Is this a production job, development program, or are you at the
    bid/budgetary stage?
  </Typography>

  <FormControl fullWidth required>
    <RadioGroup
      value={formData.projectStage}
onChange={(e) =>
  handleChange("projectStage", e.target.value)
}
    >
      {[
        "Production Job",
        "Development Program",
        "Bid/Budgetary Stage",
      ].map((option) => (
        <Box
          key={option}
          sx={{
            mb: 2,
            border: "1px solid",
            borderColor:
              formData.projectStage === option ? "#3b3b3b" : "#d8dde6",
            borderRadius: "18px",
            backgroundColor:
              formData.projectStage === option ? "#eef2f7" : "#ffffff",
          }}
        >
          <FormControlLabel
            value={option}
            control={
              <Radio
                sx={{
                  ml: 2,
                   color: "#9ca3af !important",
    "&.Mui-checked": {
      color: "#000000 !important",
    },
                      
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "#9ca3af !important",
                      },
                  
                }}
              />
            }
            label={option}
            sx={{
              width: "100%",
              m: 0,
              p: 1,
            }}
          />
        </Box>
      ))}
    </RadioGroup>
  </FormControl>
</Box>

        {!formData.weldPenetrationDepth && (
        <Box sx={{ mb: 4 }}>
  <Typography sx={{ color: "rgb(148, 163, 184)", fontSize: "18px", mb: 1,textAlign:'left' }}>
    Question 4{!formData.weldPenetrationDepth && '*'}
  </Typography>

  <Typography
    sx={{
      mb: 2,
      color: "#0f172a",
      fontSize: "16px",
      textAlign: "left",
    }}
  >
    What is the required weld penetration depth — or will best practices suffice?
  </Typography>

  <TextField
    fullWidth
    multiline
    rows={3}
    required={!formData.weldPenetrationDepth}
    value={formData.weldPenetrationDepth}
    onChange={(e) =>
      handleChange("weldPenetrationDepth", e.target.value)
    }
    placeholder="Enter required weld penetration depth or specify best practices..."
    sx={{border:'1px solid black',borderRadius:'15px'}}
  />
</Box>
        )}

          {!formData.leaktest && (
          <Box>
<Typography variant="h6" component="h2" sx={{textAlign:'left',color:"rgb(148, 163, 184)",fontSize:'18px'}}>
  Question {getQuestionNumber(5)}{!formData.leaktest && '*'}
</Typography>
<Typography
        sx={{
          mb: 3,
          fontWeight: 400,
          color: "#0f172a",
          fontSize:'16px',
          textAlign:'left'
        }}
      >
        Is fine leak testing required?
      </Typography>

      <FormControl fullWidth required={!formData.leaktest}>
        <RadioGroup
          value={formData.leaktest}
onChange={(e) =>
  handleChange("leaktest", e.target.value)
}
        >
          {["Yes", "No"].map((option) => (
            <Box
              key={option}
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor:
                  formData.leaktest === option ? "#3b3b3b" : "#d8dde6",
                borderRadius: "18px",
                backgroundColor:
                  formData.leaktest === option ? "#eef2f7" : "#ffffff",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                value={option}
                control={
                  <Radio
                    sx={{
                      ml: 2,
                       color: "#9ca3af !important",
    "&.Mui-checked": {
      color: "#000000 !important",
    },
                      
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "#9ca3af !important",
                      },

                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#1e293b",
                    }}
                  >
                    {option}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  m: 0,
                  p:1
                }}
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>
        </Box>
          )}


          <Box>
<Typography variant="h6" component="h2" sx={{textAlign:'left',color:"rgb(148, 163, 184)",fontSize:'18px'}}>
  Question {getQuestionNumber(6)}*
</Typography>
<Typography
        sx={{
          mb: 3,
          fontWeight: 400,
          color: "#0f172a",
          fontSize:'16px',
          textAlign:'left'
        }}
      >
        Is there a post-weld machining operation we should account for when planning the weld joint? 
      </Typography>

      <FormControl fullWidth required>
        <RadioGroup
          value={formData.postweldmachining}
onChange={(e) =>
  handleChange("postweldmachining", e.target.value)
}
        >
          {["Yes", "No"].map((option) => (
            <Box
              key={option}
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor:
                  formData.postweldmachining === option ? "#3b3b3b" : "#d8dde6",
                borderRadius: "18px",
                backgroundColor:
                  formData.postweldmachining === option ? "#eef2f7" : "#ffffff",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                value={option}
                control={
                  <Radio
                    sx={{
                      ml: 2,
                       color: "#9ca3af !important",
    "&.Mui-checked": {
      color: "#000000 !important",
    },
                      
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "#9ca3af !important",
                      },

                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#1e293b",
                    }}
                  >
                    {option}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  m: 0,
                  p:1
                }}
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>
        </Box>

{!formData.assemblyWeight && (
         <Box sx={{ mb: 4 }}>
  <Typography
    sx={{
      textAlign: "left",
      color: "rgb(148, 163, 184)",
      fontSize: "18px",
      mb: 1,
    }}
  >
    Question {getQuestionNumber(7)}{!formData.assemblyWeight && '*'}
  </Typography>

  <Typography
    sx={{
      mb: 2,
      color: "#0f172a",
      fontSize: "16px",
      textAlign: "left",
    }}
  >
    Approximately how much does the completed assembly weigh?
  </Typography>

  <TextField
    fullWidth
    type="number"
    placeholder="Enter weight"
    required={!formData.assemblyWeight}
  value={formData.assemblyWeight}
onChange={(e) =>
  handleChange("assemblyWeight", e.target.value)
}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          lbs
        </InputAdornment>
      ),
    }}
    sx={{
        border:'1px solid black',
        borderRadius:'15px',
      "& .MuiOutlinedInput-root": {
        borderRadius: "15px",
      },
    }}
  />
</Box>
)}

          <Box>
<Typography variant="h6" component="h2" sx={{textAlign:'left',color:"rgb(148, 163, 184)",fontSize:'18px'}}>
  Question {getQuestionNumber(8)}*
</Typography>
<Typography
        sx={{
          mb: 3,
          fontWeight: 400,
          color: "#0f172a",
          fontSize:'16px',
          textAlign:'left'
        }}
      >
        Will parts arrive at our facility clean and pre-assembled, ready for welding? 
      </Typography>

      <FormControl fullWidth required>
        <RadioGroup
          value={formData.preassembled}
onChange={(e) =>
  handleChange("preassembled", e.target.value)
}
        >
          {["Yes", "No"].map((option) => (
            <Box
              key={option}
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor:
                  formData.preassembled === option ? "#3b3b3b" : "#d8dde6",
                borderRadius: "18px",
                backgroundColor:
                  formData.preassembled === option ? "#eef2f7" : "#ffffff",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                value={option}
                control={
                  <Radio
                    sx={{
                      ml: 2,
                       color: "#9ca3af !important",
    "&.Mui-checked": {
      color: "#000000 !important",
    },
                      
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "#9ca3af !important",
                      },

                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#1e293b",
                    }}
                  >
                    {option}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  m: 0,
                  p:1
                }}
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>
        </Box>
          <Box sx={{ mb: 4,textAlign:'left' }}>
  <Typography sx={{ color: "rgb(148, 163, 184)", fontSize: "18px", mb: 1 }}>
    Question {getQuestionNumber(9)}*
  </Typography>

  <Typography sx={{ mb: 2, color: "#0f172a", fontSize: "16px" }}>
    What is the estimated annual usage quantity (EAU) for this assembly?
  </Typography>

  <TextField
    fullWidth
    required
    type="number"
    placeholder="Enter annual quantity"
    value={formData.eau}
onChange={(e) =>
  handleChange("eau", e.target.value)
}
    sx={{
        border:'1px solid black',
        borderRadius:'15px',
      "& .MuiOutlinedInput-root": {
        borderRadius: "15px",
      },
    }}
  />
</Box>
          <Box sx={{ mb: 4 ,textAlign:'left'}}>
  <Typography sx={{ color: "rgb(148, 163, 184)", fontSize: "18px", mb: 1 }}>
    Question {getQuestionNumber(10)}*
  </Typography>

  <Typography sx={{ mb: 2, color: "#0f172a", fontSize: "16px" }}>
    What industry or application are these assemblies intended for?
  </Typography>

  <TextField
    select
    fullWidth
    required
    value={formData.industry}
onChange={(e) =>
  handleChange("industry", e.target.value)
}
     SelectProps={{
    IconComponent: KeyboardArrowDownIcon,
  }}
  sx={{
    border:'1px solid black',
    borderRadius:'15px',
    textAlign:'left',
    "& .MuiSelect-icon": {
      color: "#64748b",
      right: 12,
    },
  }}
  >
    <MenuItem value="">Select Industry</MenuItem>
    <MenuItem value="Aerospace">Aerospace</MenuItem>
    <MenuItem value="Medical">Medical</MenuItem>
    <MenuItem value="Defense">Defense</MenuItem>
    <MenuItem value="Semiconductor">Semiconductor</MenuItem>
    <MenuItem value="Industrial">Industrial</MenuItem>
    <MenuItem value="Other">Other</MenuItem>
  </TextField>
</Box>
          <Box sx={{ mb: 4,textAlign:'left' }}>
  <Typography sx={{ color: "rgb(148, 163, 184)", fontSize: "18px", mb: 1 }}>
    Question {getQuestionNumber(11)}
  </Typography>

  <Typography sx={{ mb: 2, color: "#0f172a", fontSize: "16px" }}>
    Are there any risks, special handling requirements, or sensitivities associated with this project we should be aware of?
  </Typography>

  <TextField
    fullWidth
    multiline
    rows={4}
    placeholder="Enter details..."
    value={formData.risks}
onChange={(e) =>
  handleChange("risks", e.target.value)
}
    sx={{
        border:'1px solid black',
        borderRadius:'15px',
      "& .MuiOutlinedInput-root": {
        borderRadius: "15px",
      },
    }}
  />
</Box>
         <Box sx={{ mb: 4,textAlign:'left' }}>
  <Typography sx={{ color: "rgb(148, 163, 184)", fontSize: "18px", mb: 1 }}>
    Question {getQuestionNumber(12)}
  </Typography>

  <Typography sx={{ mb: 2, color: "#0f172a", fontSize: "16px" }}>
    What is the best billing address and shipping address for this project?
  </Typography>

  <TextField
    fullWidth
    multiline
    rows={3}
    label="Billing Address"
    value={formData.billingAddress}
onChange={(e) =>
  handleChange("billingAddress", e.target.value)
}
    sx={{
      mb: 2,
      borderRadius:'15px',
      "& .MuiOutlinedInput-root": {
        borderRadius: "15px",
        
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        border: "1px solid black",
      },
      "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        border: "1px solid black",
      },
    }}
  />

  <TextField
    fullWidth
    multiline
    rows={3}
    label="Shipping Address"
    value={formData.shippingAddress}
onChange={(e) =>
  handleChange("shippingAddress", e.target.value)
}
    sx={{
      borderRadius:'15px',
      "& .MuiOutlinedInput-root": {
        borderRadius: "15px",
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        border: "1px solid black",
      },
      "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        border: "1px solid black",
      },
    }}
  />
</Box>
          <Box sx={{ mb: 4 }}>
  <Typography
    sx={{
      textAlign: "left",
      color: "rgb(148, 163, 184)",
      fontSize: "18px",
      mb: 1,
    }}
  >
    Question {getQuestionNumber(13)}
  </Typography>

  <Typography
    sx={{
      mb: 2,
      color: "#0f172a",
      fontSize: "16px",
      textAlign: "left",
    }}
  >
    How did you hear about EB Industries?
  </Typography>

  <TextField
    select
    fullWidth
    value={formData.hearAboutUs}
onChange={(e) =>
  handleChange("hearAboutUs", e.target.value)
}
     SelectProps={{
    IconComponent: KeyboardArrowDownIcon,
  }}
  sx={{
    border:'1px solid black',
    borderRadius:'15px',
    textAlign:'left',
    "& .MuiSelect-icon": {
      color: "#64748b",
      right: 12,
    },
  }}
  >
    <MenuItem value="">Select an option</MenuItem>
    <MenuItem value="Google Search">Google Search</MenuItem>
    <MenuItem value="Referral">Referral</MenuItem>
    <MenuItem value="LinkedIn">LinkedIn</MenuItem>
    <MenuItem value="Trade Show">Trade Show</MenuItem>
    <MenuItem value="Website">Website</MenuItem>
    <MenuItem value="Existing Customer">Existing Customer</MenuItem>
    <MenuItem value="Other">Other</MenuItem>
  </TextField>
</Box>
          <Box>
<Typography variant="h6" component="h2" sx={{textAlign:'left',color:"rgb(148, 163, 184)",fontSize:'18px'}}>
  Question {getQuestionNumber(14)}
</Typography>
<Typography
        sx={{
          mb: 3,
          fontWeight: 400,
          color: "#0f172a",
          fontSize:'16px',
          textAlign:'left'
        }}
      >
        Do you have other assemblies where you currently outsource the welding?
      </Typography>

      <FormControl fullWidth>
        <RadioGroup
        value={formData.outsourceDetails}
onChange={(e) =>
  handleChange("outsourceDetails", e.target.value)
}
        >
          {["Yes", "No"].map((option) => (
            <Box
              key={option}
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor:
                  formData.outsourceDetails === option ? "#3b3b3b" : "#d8dde6",
                borderRadius: "18px",
                backgroundColor:
                  formData.outsourceDetails === option ? "#eef2f7" : "#ffffff",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                value={option}
                control={
                  <Radio
                    sx={{
                      ml: 2,
                       color: "#9ca3af !important",
    "&.Mui-checked": {
      color: "#000000 !important",
    },
                      
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "#9ca3af !important",
                      },

                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#1e293b",
                    }}
                  >
                    {option}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  m: 0,
                  p:1
                }}
              />
            </Box>
          ))}
        </RadioGroup>
        {formData.outsourceDetails === "Yes" && (
      <TextField
        fullWidth
        multiline
        rows={4}
        sx={{ mt: 2,border:'1px solid black',borderRadius:'15px' }}
        placeholder="Please provide details about the assemblies..."
        value={formData.weldPenetrationDepth}
        onChange={(e) => handleChange("weldPenetrationDepth", e.target.value)}
      />
    )}
      </FormControl>
        </Box>

        <Button 
          sx={{
            backgroundColor: isFormValid() ? '#1b184d' : '#cccccc',
            color: '#ffff',
            width:'100%',
            marginTop:'10px',
            '&:disabled': {
              backgroundColor: '#cccccc',
              color: '#666666',
              cursor: 'not-allowed'
            }
          }} 
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting}
        >
          {isSubmitting ? 'Submitting ...' : 'Submit'}
        </Button>
        </Box>


    </Box>
  );
};

export default Questions;